/**
 * First-Value Guided Onboarding Context
 * 
 * Manages the guided onboarding flow state for new tenants.
 * Provides context to suppress old OnboardingCard and control navigation.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { authFetch } from '../auth/authApi';

// Note: authFetch automatically adds /api prefix and backend URL

const FirstValueOnboardingContext = createContext(null);

// SessionStorage flag used to suppress email-verification redirects ONLY during
// an active FVO session (and the immediate post-FVO continuation flow on
// /customers). Cleared explicitly via endFvoSession() once the user has
// completed the post-FVO continuation (e.g., created their first real customer)
// or on logout (whole sessionStorage is wiped).
const FVO_SESSION_FLAG = 'fvo_session_in_progress';

export function setFvoSessionFlag() {
  try { sessionStorage.setItem(FVO_SESSION_FLAG, '1'); } catch (_) {}
}
export function clearFvoSessionFlag() {
  try { sessionStorage.removeItem(FVO_SESSION_FLAG); } catch (_) {}
}
export function isFvoSessionActive() {
  try { return sessionStorage.getItem(FVO_SESSION_FLAG) === '1'; } catch (_) { return false; }
}

export function FirstValueOnboardingProvider({ children }) {
  const location = useLocation();
  const [state, setState] = useState({
    loading: true,
    active: false,
    step: null,
    starterJobId: null,
    starterCustomerId: null,
    starterVisitId: null,
    batchId: null,
    completedAt: null,
  });

  const fetchStatus = useCallback(async () => {
    try {
      const res = await authFetch('/first-value-onboarding/status');
      if (res.ok) {
        const data = await res.json();
        // If FVO is active, mark session flag so verification redirects are
        // suppressed during the entire FVO experience.
        if (data.active) {
          setFvoSessionFlag();
        }
        setState({
          loading: false,
          active: data.active,
          step: data.step,
          starterJobId: data.starter_job_id,
          starterCustomerId: data.starter_customer_id,
          starterVisitId: data.starter_visit_id,
          batchId: data.onboarding_batch_id,
          completedAt: data.completed_at || null,
        });
      } else {
        setState(prev => ({ ...prev, loading: false, active: false }));
      }
    } catch (error) {
      console.error('[FirstValueOnboarding] Failed to fetch status:', error);
      setState(prev => ({ ...prev, loading: false, active: false }));
    }
  }, []);

  // Fetch status when location changes to a protected route
  // This handles the case where user signs up and gets redirected
  useEffect(() => {
    // Only fetch on protected routes (not on /login, /signup, etc.)
    const publicRoutes = ['/login', '/signup', '/reset-password', '/verify-email', '/auth/callback', '/public'];
    const isPublicRoute = publicRoutes.some(route => location.pathname.startsWith(route));
    
    if (!isPublicRoute) {
      fetchStatus();
    } else {
      setState(prev => ({ ...prev, loading: false, active: false }));
    }
  }, [location.pathname, fetchStatus]);

  const scheduleVisit = useCallback(async () => {
    try {
      const res = await authFetch('/first-value-onboarding/schedule-visit', {
        method: 'POST',
      });
      if (res.ok) {
        const data = await res.json();
        setState(prev => ({
          ...prev,
          step: data.step,
          starterVisitId: data.visit_id,
        }));
        return { success: true, visitId: data.visit_id };
      }
      return { success: false };
    } catch (error) {
      console.error('[FirstValueOnboarding] Failed to schedule visit:', error);
      return { success: false, error };
    }
  }, []);

  const convertToReal = useCallback(async () => {
    try {
      const res = await authFetch('/first-value-onboarding/convert-to-real', {
        method: 'POST',
      });
      if (res.ok) {
        const data = await res.json();
        setState(prev => ({
          ...prev,
          active: false,
          step: 'completed',
        }));
        return { 
          success: true, 
          jobId: data.starter_job_id, 
          customerId: data.starter_customer_id 
        };
      }
      return { success: false };
    } catch (error) {
      console.error('[FirstValueOnboarding] Failed to convert:', error);
      return { success: false, error };
    }
  }, []);

  const startFresh = useCallback(async () => {
    try {
      const res = await authFetch('/first-value-onboarding/start-fresh', {
        method: 'POST',
      });
      if (res.ok) {
        setState(prev => ({
          ...prev,
          active: false,
          step: 'completed',
        }));
        return { success: true };
      }
      return { success: false };
    } catch (error) {
      console.error('[FirstValueOnboarding] Failed to start fresh:', error);
      return { success: false, error };
    }
  }, []);

  const value = {
    ...state,
    fetchStatus,
    scheduleVisit,
    convertToReal,
    startFresh,
    endFvoSession: clearFvoSessionFlag,
    // Helper to check if we're in guided mode
    isGuided: state.active && state.step !== 'completed',
    // Suppress the legacy generic OnboardingCard for any tenant who has
    // entered (active) OR completed first-value onboarding. Existing tenants
    // who never went through FVO have active=false AND completedAt=null and
    // continue to see the legacy card — they are unaffected.
    suppressOldOnboarding: state.active || Boolean(state.completedAt),
  };

  return (
    <FirstValueOnboardingContext.Provider value={value}>
      {children}
    </FirstValueOnboardingContext.Provider>
  );
}

export function useFirstValueOnboarding() {
  const context = useContext(FirstValueOnboardingContext);
  if (!context) {
    // Return a safe default if used outside provider
    return {
      loading: false,
      active: false,
      isGuided: false,
      suppressOldOnboarding: false,
      endFvoSession: clearFvoSessionFlag,
    };
  }
  return context;
}
